import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers, Paintbrush, LayoutGrid, Building2, Home, ArrowRight, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../data/translations';
import { serviceImages } from '../data/references';
import { SEO } from '../components/SEO';

export default function ServicesPage() {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const services = [
    { id: 'gipserarbeiten', icon: Layers, title: t.services.gipserarbeiten.title, description: t.services.gipserarbeiten.description, image: serviceImages.gipserarbeiten, color: '#E31C23' },
    { id: 'malerarbeiten', icon: Paintbrush, title: t.services.malerarbeiten.title, description: t.services.malerarbeiten.description, image: serviceImages.malerarbeiten, color: '#00A651' },
    { id: 'trockenbau', icon: LayoutGrid, title: t.services.trockenbau.title, description: t.services.trockenbau.description, image: serviceImages.trockenbau, color: '#C41230' },
    { id: 'aussenfassaden', icon: Building2, title: t.services.aussenfassaden.title, description: t.services.aussenfassaden.description, image: serviceImages.aussenfassaden, color: '#E31C23' },
    { id: 'innenausbau', icon: Home, title: t.services.innenausbau.title, description: t.services.innenausbau.description, image: serviceImages.innenausbau, color: '#00A651' }
  ];

  return (
    <main className="pt-20" data-testid="services-page">
      <SEO
        title="Dienstleistungen – Gipser, Maler, Trockenbau, Fassaden, Innenausbau"
        path="/dienstleistungen"
        description="Alle Dienstleistungen von BENI Gipsergeschäft: Gipserarbeiten, Malerarbeiten, Trockenbau, Aussenfassaden und Innenausbau in Aesch und Region Basel. Kostenlose Offerte!"
        keywords="Gipserarbeiten Aesch, Malerarbeiten Basel, Trockenbau Reinach, Fassadenarbeiten Münchenstein, Innenausbau Aesch, Gipser Dienstleistungen Basel-Landschaft, Verputzarbeiten, Stuckarbeiten, Fassadensanierung, Wärmedämmung"
        breadcrumbs={[{ name: 'Startseite', path: '/' }, { name: 'Dienstleistungen', path: '/dienstleistungen' }]}
      />
      <section className="py-20 md:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="services-page-title">{t.services.title}</h1>
            <p className="text-xl text-gray-300 max-w-2xl">{t.services.subtitle}</p>
            <div className="w-20 h-1 bg-[#E31C23] mt-8" />
          </motion.div>
        </div>
      </section>
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-16">
          {services.map((service, index) => (
            <motion.div key={service.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-testid={`service-section-${service.id}`}>
              <div className={`relative overflow-hidden ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <img src={service.image} alt={service.title} className="w-full h-80 lg:h-[400px] object-cover" />
                <div className="absolute top-6 left-6 w-16 h-16 flex items-center justify-center" style={{ backgroundColor: service.color }}><service.icon className="w-8 h-8 text-white" /></div>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">{service.title}</h2>
                <p className="text-gray-600 leading-relaxed mb-8">{service.description}</p>
                <Link to={`/dienstleistungen/${service.id}`} className="inline-flex items-center space-x-2 bg-[#1A1A1A] text-white px-8 py-4 font-semibold hover:bg-[#333] transition-colors group"><span>{t.services.learnMore}</span><ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      <section className="py-20 bg-[#E31C23]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">{t.services.readyTitle}</h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">{t.services.readyText}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt" className="inline-flex items-center justify-center space-x-2 bg-white text-[#E31C23] px-8 py-4 font-semibold hover:bg-gray-100 transition-colors"><span>{t.contact.submit}</span><ArrowRight className="w-5 h-5" /></Link>
              <a href="tel:+41793591862" className="inline-flex items-center justify-center space-x-2 border-2 border-white text-white px-8 py-4 font-semibold hover:bg-white hover:text-[#E31C23] transition-colors"><Phone className="w-5 h-5" /><span>{t.contact.call_now}</span></a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
